// client/src/components/paymentUploads/PaymentUploadForm.js
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import paymentUploadService from '../../services/paymentUploadService';

const PaymentUploadForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileName, setFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [showFormat, setShowFormat] = useState(false);
  
  // Handle file upload with react-dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setFileName(file.name);
    setExcelFile(file);
    setUploadError('');
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!excelFile) {
      setError(t('pleaseSelectFileFirst'));
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', excelFile);
      
      const response = await paymentUploadService.uploadPaymentFile(formData);
      setUploadResult(response.data);
      setSuccess(t('paymentFileUploadedSuccess'));
      setLoading(false);
    } catch (err) {
      setError(t('errorUploadingPaymentFile'));
      setLoading(false);
    }
  };
  
  // Toggle showing file format help
  const toggleFormatHelp = () => {
    setShowFormat(!showFormat);
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('uploadPaymentFile')}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        {!uploadResult ? (
          <>
            <Typography variant="h6" gutterBottom>
              {t('uploadPaymentExcel')}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography>{t('uploadPaymentFileInstructions')}</Typography>
              <Button 
                size="small" 
                sx={{ mt: 1 }} 
                onClick={toggleFormatHelp}
                startIcon={<HelpIcon />}
              >
                {showFormat ? t('hideFileFormat') : t('showFileFormat')}
              </Button>
            </Alert>
            
            {showFormat && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('requiredFileFormat')}:
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('fileFormatDescription')}
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={3}>
                    <strong>Item ID</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('itemIdDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>National ID</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('nationalIdDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>Name (EN)</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('nameEnDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>Name (AR)</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('nameArDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>Total Value</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('totalValueDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>Payment Date</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('paymentDateDescription')}
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <strong>Payment Reference</strong> ({t('required')})
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {t('paymentReferenceDescription')}
                  </Grid>
                </Grid>
                
                <Alert severity="warning" variant="outlined">
                  {t('fileFormatWarning')}
                </Alert>
              </Paper>
            )}
            
            <Box 
              {...getRootProps()} 
              sx={{ 
                border: '2px dashed #ccc',
                borderRadius: '4px',
                p: 3,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main'
                }
              }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography>{t('dropExcelHere')}</Typography>
              ) : (
                <Box>
                  <UploadIcon fontSize="large" color="primary" sx={{ mb: 1 }} />
                  <Typography>{t('dragDropExcelOrClick')}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('acceptedFormats')}: .xlsx, .xls
                  </Typography>
                </Box>
              )}
            </Box>
            
            {uploadError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {uploadError}
              </Alert>
            )}
            
            {fileName && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography>{t('fileSelected')}: {fileName}</Typography>
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/payment-uploads')}
                startIcon={<ArrowBackIcon />}
              >
                {t('cancel')}
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || !excelFile}
                startIcon={loading ? <CircularProgress size={24} /> : <UploadIcon />}
              >
                {loading ? t('uploading') : t('uploadAndProcess')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              {t('uploadResults')}
            </Typography>
            
            <Alert 
              severity={uploadResult.errors ? "warning" : "success"} 
              sx={{ mb: 3 }}
            >
              <Typography>
                {t('processedRecords')}: {uploadResult.total_records}
              </Typography>
              <Typography>
                {t('successfulMatches')}: {uploadResult.matched_records}
              </Typography>
              {uploadResult.errors && (
                <Typography>
                  {t('failedRecords')}: {uploadResult.total_records - uploadResult.matched_records}
                </Typography>
              )}
            </Alert>
            
            {uploadResult.errors && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('errors')}:
                </Typography>
                <List dense>
                  {uploadResult.errors.slice(0, 5).map((err, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={err.error}
                        secondary={`${t('row')}: ${JSON.stringify(err.row)}`}
                      />
                    </ListItem>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <ListItem>
                      <ListItemText 
                        primary={t('andMoreErrors', { count: uploadResult.errors.length - 5 })}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/payment-uploads')}
              >
                {t('backToUploads')}
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setExcelFile(null);
                  setFileName('');
                  setUploadResult(null);
                  setSuccess('');
                }}
              >
                {t('uploadAnotherFile')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PaymentUploadForm;